import { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import {
  PlusIcon,
  ChevronRightIcon,
  ChevronUpDownIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  ClipboardDocumentListIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XCircleIcon,
  PrinterIcon,
  DocumentTextIcon,
} from "@heroicons/react/24/outline";
import { useTranslation } from "react-i18next";
import { debounce } from "lodash";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ResourceLoading } from "../../../utils/ResourceLoading";
import { ResourceError } from "../../../utils/ResourceError";
import { ResourceEmpty } from "../../../utils/ResourceEmpty";
import { ModuleFilter } from "../../../utils/ModuleFilter";
import ChecklistTypeModal from "./checklist-type-modal";
import ChecklistTypeHseModal from "./checklist-type-hse-modal";
import { fetchChecklistTypes, fetchChecklistTypePdf } from "../../../../api/quality/checklistType";

const CHECKLIST_TYPE_FIELDS =
  "id,displayTitle,title,description,active,created_at,updated_at,hse_document";

export default function ChecklistTypesStacklist() {
  const [modalOpen, setModalOpen] = useState(false);
  const [hseModalOpen, setHseModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [hseItemId, setHseItemId] = useState(null);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [checklistTypes, setChecklistTypes] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);
  const [printingId, setPrintingId] = useState(null);

  const handleOpenHseModal = (event, item) => {
    event.stopPropagation();
    if (!item?.id) return;
    setHseItemId(item.id);
    setHseModalOpen(true);
  };

  const handlePrintType = async (event, item) => {
    event.stopPropagation();
    if (!item?.id) return;
    setPrintingId(item.id);
    const res = await fetchChecklistTypePdf(item.id);
    setPrintingId(null);
    if (res?.pdf_url) {
      window.open(res.pdf_url, "_blank", "noopener");
    } else {
      toast.error(t("quality.checklist.type.print_failed"));
    }
  };

  const [controller, setController] = useState({
    query: "",
    sort: "ASC",
    sortBy: "title",
    include_inactive: false,
    per_page: 100,
    page: 1,
  });

  const fetchTypes = useCallback(
    debounce(async (params) => {
      try {
        setIsLoading(true);
        setError(false);
        const response = await fetchChecklistTypes({
          ...params,
          fields: CHECKLIST_TYPE_FIELDS,
          include_inactive: params.include_inactive ? "true" : undefined,
        });
        setChecklistTypes(response?.data?.data || []);
        setTotalCount(response?.data?.totalCount ?? 0);
      } catch (err) {
        console.error("Error fetching checklist types:", err);
        setError(true);
        toast.error(t("Kunne ikke laste sjekklistetyper"));
      } finally {
        setIsLoading(false);
      }
    }, 250),
    [t]
  );

  useEffect(() => {
    fetchTypes(controller);
    return () => fetchTypes.cancel();
  }, [controller, fetchTypes]);

  useEffect(() => {
    const hseTypeId = searchParams.get("hse_type");
    if (!hseTypeId || isLoading) return;

    setHseItemId(hseTypeId);
    setHseModalOpen(true);

    const next = new URLSearchParams(searchParams);
    next.delete("hse_type");
    setSearchParams(next, { replace: true });
  }, [searchParams, isLoading, setSearchParams]);

  const handleFiltersChange = (filters) => {
    setController((prev) => ({
      ...prev,
      page: 1,
      query: filters.query || "",
      include_inactive: !!filters.include_inactive,
    }));
  };

  const handleSort = (column) => {
    setController((prev) => ({
      ...prev,
      sortBy: column,
      sort:
        prev.sortBy === column && prev.sort === "ASC" ? "DESC" : "ASC",
    }));
  };

  const handleCallback = (data) => {
    setChecklistTypes((prev) => {
      const next = [...prev];
      const index = next.findIndex((item) => item?.id === data?.id);
      if (index !== -1) {
        next[index] = data;
      } else {
        next.unshift(data);
      }
      return next;
    });
    setModalOpen(false);
    fetchTypes(controller);
  };

  const getInitialFilters = () => ({
    query: controller.query,
    include_inactive: controller.include_inactive,
  });

  const customCheckboxButtonFilters = [
    {
      key: "include_inactive",
      label: t("Inaktive sjekklistetyper"),
      icon: ExclamationTriangleIcon,
      defaultValue: false,
    },
  ];

  const SortIcon = ({ column }) => {
    if (controller.sortBy !== column) {
      return <ChevronUpDownIcon className="ml-1 h-4 w-4 text-slate-400" />;
    }
    return controller.sort === "ASC" ? (
      <ChevronUpIcon className="ml-1 h-4 w-4 text-slate-700" />
    ) : (
      <ChevronDownIcon className="ml-1 h-4 w-4 text-slate-700" />
    );
  };

  const formatDate = (value) => {
    if (!value) return "—";
    return new Date(value).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  return (
    <>
      <div className="space-y-4">
        <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
          <h3 className="mb-2 text-sm font-semibold text-blue-900">
            {t("quality.checklist.type.hse.title")}
          </h3>
          <p className="text-sm text-blue-700">
            {t("quality.checklist.type.hse.settings_hint")}
          </p>
        </div>

        <ModuleFilter
          filterCard
          disabled={isLoading}
          onFiltersChange={handleFiltersChange}
          initialFilters={getInitialFilters()}
          searchPlaceholder={t("Søk sjekklistetyper...")}
          hideStatusFilter
          customCheckboxButtonFilters={customCheckboxButtonFilters}
        />

        <div className="overflow-hidden rounded-[18px] border border-slate-200 bg-white shadow-[0_1px_2px_rgba(0,0,0,0.04),0_6px_22px_rgba(0,0,0,0.07)]">
          <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
            <div className="flex items-center gap-2">
              <ClipboardDocumentListIcon className="h-5 w-5 text-slate-500" />
              <h3 className="text-[15px] font-semibold text-slate-900">
                {t("Sjekklistetyper")}
                {!isLoading && (
                  <span className="ml-2 font-normal text-slate-500">
                    ({totalCount})
                  </span>
                )}
              </h3>
            </div>
            <button
              type="button"
              onClick={() => setModalOpen(true)}
              className="inline-flex items-center gap-2 rounded-full bg-slate-900 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-slate-800"
            >
              <PlusIcon className="h-4 w-4" />
              {t("Ny sjekklistetype")}
            </button>
          </div>

          {isLoading ? (
            <div className="p-8">
              <ResourceLoading size="base" />
            </div>
          ) : error ? (
            <div className="p-8">
              <ResourceError errors={[t("Kunne ikke laste sjekklistetyper")]} />
            </div>
          ) : checklistTypes.length === 0 ? (
            <div className="p-8">
              <ResourceEmpty message={t("Ingen sjekklistetyper funnet")} />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-slate-100">
                <thead className="bg-slate-50/80">
                  <tr>
                    <th
                      scope="col"
                      className="py-3.5 pl-5 pr-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500"
                    >
                      <button
                        type="button"
                        onClick={() => handleSort("title")}
                        className="inline-flex items-center hover:text-slate-800"
                      >
                        {t("Tittel")}
                        <SortIcon column="title" />
                      </button>
                    </th>
                    <th
                      scope="col"
                      className="px-3 py-3.5 text-left text-xs font-semibold uppercase tracking-wide text-slate-500"
                    >
                      {t("Beskrivelse")}
                    </th>
                    <th
                      scope="col"
                      className="px-3 py-3.5 text-left text-xs font-semibold uppercase tracking-wide text-slate-500"
                    >
                      {t("Status")}
                    </th>
                    <th
                      scope="col"
                      className="px-3 py-3.5 text-left text-xs font-semibold uppercase tracking-wide text-slate-500"
                    >
                      {t("quality.checklist.type.hse.column")}
                    </th>
                    <th
                      scope="col"
                      className="px-3 py-3.5 text-left text-xs font-semibold uppercase tracking-wide text-slate-500"
                    >
                      <button
                        type="button"
                        onClick={() => handleSort("created_at")}
                        className="inline-flex items-center hover:text-slate-800"
                      >
                        {t("Opprettet")}
                        <SortIcon column="created_at" />
                      </button>
                    </th>
                    <th scope="col" className="relative py-3.5 pl-3 pr-5">
                      <span className="sr-only">{t("Handlinger")}</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 bg-white">
                  {checklistTypes.map((item) => {
                    const isActive = item.active !== false;
                    return (
                      <tr
                        key={item.id}
                        onClick={() =>
                          navigate(
                            "/dashboard/hse-ks/settings/checklistType/" + item.id
                          )
                        }
                        className="cursor-pointer transition-colors hover:bg-slate-50"
                      >
                        <td className="whitespace-nowrap py-4 pl-5 pr-3 text-sm font-medium text-slate-900">
                          {item.displayTitle || item.title || "—"}
                        </td>
                        <td className="max-w-md px-3 py-4 text-sm text-slate-500">
                          <span className="line-clamp-2">
                            {item.description || "—"}
                          </span>
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm">
                          <span
                            className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                              isActive
                                ? "bg-emerald-50 text-emerald-700"
                                : "bg-slate-100 text-slate-600"
                            }`}
                          >
                            {isActive ? (
                              <CheckCircleIcon className="h-3.5 w-3.5" />
                            ) : (
                              <XCircleIcon className="h-3.5 w-3.5" />
                            )}
                            {isActive ? t("Aktiv") : t("Inaktiv")}
                          </span>
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm">
                          <button
                            type="button"
                            title={t("quality.checklist.type.hse.configure")}
                            onClick={(event) => handleOpenHseModal(event, item)}
                            className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold transition-colors hover:opacity-80 ${
                              item.hse_document?.id
                                ? "bg-blue-50 text-blue-700"
                                : "bg-slate-100 text-slate-500"
                            }`}
                          >
                            {item.hse_document?.id
                              ? t("quality.checklist.type.hse.registered_short")
                              : t("quality.checklist.type.hse.not_registered")}
                          </button>
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-slate-500">
                          {formatDate(item.created_at)}
                        </td>
                        <td className="whitespace-nowrap py-4 pl-3 pr-5 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              type="button"
                              title={t("quality.checklist.type.hse.configure")}
                              onClick={(event) => handleOpenHseModal(event, item)}
                              className="inline-flex items-center gap-1 rounded-full border border-slate-300 bg-white px-2.5 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                            >
                              <DocumentTextIcon className="h-3.5 w-3.5" />
                              <span className="hidden sm:inline">
                                {t("quality.checklist.type.hse.configure_short")}
                              </span>
                            </button>
                            <button
                              type="button"
                              title={t("quality.checklist.type.print")}
                              disabled={printingId === item.id}
                              onClick={(event) => handlePrintType(event, item)}
                              className="rounded-full p-1.5 text-slate-500 hover:bg-slate-100 hover:text-slate-800 disabled:opacity-50"
                            >
                              <PrinterIcon className="h-4 w-4" />
                            </button>
                            <ChevronRightIcon className="h-5 w-5 text-slate-400" />
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <ChecklistTypeModal
        onClose={() => {
          setModalOpen(false);
          setSelectedItem(null);
        }}
        open={modalOpen}
        itemId={selectedItem}
        callback={(data) => handleCallback(data)}
      />

      <ChecklistTypeHseModal
        open={hseModalOpen}
        itemId={hseItemId}
        onClose={() => {
          setHseModalOpen(false);
          setHseItemId(null);
        }}
        onRegistered={(data) => {
          if (data?.id) {
            setChecklistTypes((prev) =>
              prev.map((item) => (item.id === data.id ? { ...item, ...data } : item))
            );
          }
        }}
      />
    </>
  );
}
